import React, { useState } from "react";
import { Search, BadgeCheck, ShieldAlert, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function VerificationPanel({ compact = false }) {
  const [cid, setCid] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    if (!cid.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/verify/${cid.trim()}`);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const statusClass = result?.status === "Successfully Completed"
    ? "vj-status-completed"
    : result?.status === "Cancelled"
      ? "vj-status-cancelled"
      : "vj-status-ongoing";

  return (
    <div className={compact ? "" : ""} data-testid="verification-panel">
      <form onSubmit={search} className="flex flex-col sm:flex-row gap-3" data-testid="verify-form">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            data-testid="verify-input"
            value={cid}
            onChange={(e) => setCid(e.target.value)}
            placeholder="Enter Credential ID  e.g. VAJRON-INT-2026-0001"
            className="vj-input font-mono tracking-wider uppercase"
            style={{ paddingLeft: 44 }}
          />
        </div>
        <button type="submit" disabled={loading} data-testid="verify-submit" className="vj-btn-primary">
          {loading ? <Loader2 size={16} className="animate-spin"/> : <BadgeCheck size={16}/>}
          Verify Credential
        </button>
      </form>

      {error && (
        <div className="mt-6 border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="verify-error">
          <ShieldAlert className="text-red-600 mt-0.5" size={18}/>
          <div>
            <div className="font-semibold text-red-700">Credential not found</div>
            <div className="text-sm text-red-600 mt-1">{error}</div>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-8 border border-slate-200 bg-white" data-testid="verify-result-card">
          <div className="vj-dark px-7 py-6 flex items-center justify-between">
            <div>
              <div className="overline text-slate-400">Credential Verification Result</div>
              <div className="font-mono text-white text-xl mt-1.5 tracking-wide" data-testid="verify-credential-id">
                {result.credential_id}
              </div>
            </div>
            <div className="vj-verified" data-testid="verify-badge">
              <BadgeCheck size={14}/> VERIFIED
            </div>
          </div>
          <div className="px-7 py-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
            <Field label="Name" value={result.name} testid="verify-name"/>
            <Field label="Institute" value={result.institute} testid="verify-institute"/>
            <Field label="Role" value={result.role} testid="verify-role"/>
            <Field label="Department" value={result.department} testid="verify-department"/>
            <Field label="Internship Type" value={result.internship_type} testid="verify-type"/>
            <Field label="Mode" value={result.mode} testid="verify-mode"/>
            <Field label="Start Date" value={result.start_date} testid="verify-start"/>
            <Field label="End Date" value={result.end_date} testid="verify-end"/>
            <div>
              <div className="overline">Status</div>
              <div className="mt-1.5">
                <span className={`vj-status-pill ${statusClass}`} data-testid="verify-status">{result.status}</span>
              </div>
            </div>
            <div>
              <div className="overline">Verification</div>
              <div className="mt-1.5 text-emerald-700 font-mono text-sm font-semibold">PERMANENTLY VERIFIED</div>
            </div>
          </div>
          <div className="border-t border-slate-200 px-7 py-4 flex items-center justify-between text-xs text-slate-500">
            <span>Issued by VAJRON Global Tech Pvt. Ltd.</span>
            <span className="font-mono">vajron.global / verify</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1.5 text-[#0F172A] text-[15px]" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}
